import React, {useEffect, useMemo, useState} from 'react';

import {
    Button,
    HStack,
    Input,
    NumberInput,
    Spinner,
    VStack,
    Select,
    Box,
    Portal,
    Stack,
    For,
    createListCollection
} from '@chakra-ui/react';

import {useForm} from '@/uses/useForm';
import {useApi} from '@/uses/useApi';
import {Field, FileInput, LanguageField, NestedSelect} from "@/components";
import {useLanguage} from "@/contexts/languageContext";
import objectUtil from "@/utils/objectUtil";

export const CreateOrEditResourceCategory = (props) => {
    const categoryId = props?.id;

    const [state, setState] = useState(-1);
    const {getLangObject, language, t} = useLanguage();
    const [resourceCategory, setResourceCategory] = useState(null);
    const [categories, setCategories] = useState(null);
    const [contactIndicator, setContactIndicator] = useState('33');
    const [contactNumber, setContactNumber] = useState('');
    const api = useApi();
    const {formData, formFieldErrors, handleInputChange, handleInputChanges, handleFormSubmit} = useForm(
        {
            label: getLangObject(),
            categoryId: null,
            entityType: 'resource',
            thumbnail: null,
            sortOrder: 0,
            contact: {
                name: '',
                number: ''
            }
        },
        {
            label: {
                custom: {
                    function: (value) => {
                        return Object.values(value).some((value) => value && value.length >= 3)
                    },
                    message: t('errors.pleaseFillOrKeepEmpty')
                },
            },
        }
    );

    useEffect(() => {
        setState(() => 0);
        api.ApiService.get('category', {
            getAll: 'bool:true'
        })
            .then((res) => {
                if (res?.data?.type === 'success' && res?.data?.value) {
                    let tmp = objectUtil.plainToTree(res.data.value ?? [], 'id', 'parent');
                    tmp = objectUtil.treeToPlain(tmp, 'children', 'id', 'parent');
                    setCategories(() => tmp);
                }
            })
        if (categoryId) {
            api.ApiService.getOne('category', categoryId, {
                attributes: ['id', 'label', 'thumbnail', 'categoryId', 'sortOrder', 'contact'],
            })
                .then((res) => {
                    if (res?.data?.type === 'success' && res?.data?.value) {
                        setResourceCategory(() => res.data.value);
                    }
                })
        }
    }, []);

    useEffect(() => {
        if (resourceCategory !== null) {
            handleInputChanges(resourceCategory);
            if (resourceCategory.contact?.number) {
                const match = resourceCategory.contact.number.match(/^\+?(\d+)(.*)$/);
                if (match) {
                    const foundIndicator = indicator.find(i => match[1].startsWith(i.value));
                    if (foundIndicator) {
                        setContactIndicator(foundIndicator.value);
                        setContactNumber(match[1].substring(foundIndicator.value.length) + match[2]);
                    }
                }
            }
        }
        if ((!categoryId || resourceCategory !== null) && categories !== null) {
            setState(() => 1)
        }
    }, [categoryId, resourceCategory, categories]);

    const categoriesTree = useMemo(() => {
        return (categories ?? []).map((e) => ({...e, value: e.id, label: e?.[`${language}Label`] ?? ''}))
    }, [categories, language]);

    const indicator = [
        {value: '33', label: 'France +33', short: 'fr', length: 9},
        {value: '32', label: 'Belgique +32', short: 'be', length: 9},
        {value: '41', label: 'Suisse +41', short: 'ch', length: 9},
        {value: '44', label: 'Royaume-Uni +44', short: 'uk', length: 10},
    ];

    useEffect(() => {
        const fullNumber = contactNumber ? `+${contactIndicator}${contactNumber}` : '';

        if (formData.contact && formData.contact.number !== fullNumber) {
            handleInputChange('contact', {
                ...formData.contact,
                number: fullNumber
            });
        } else if (!formData.contact) {
            handleInputChange('contact', {
                name: '',
                number: fullNumber
            });
        }
    }, [contactIndicator, contactNumber]);

    useEffect(() => {
        if (formData.categoryId !== null && formData.categoryId !== undefined) {
            if (formData.contact && (formData.contact.name || formData.contact.number)) {
                handleInputChange('contact', {
                    name: '',
                    number: ''
                });
                setContactNumber('');
            }
        }
    }, [formData.categoryId]);

    const handlePhoneNumberChange = (value) => {
        let cleaned = value.replace(/\D/g, '');

        if (cleaned.startsWith('0')) {
            cleaned = cleaned.substring(1);
        }

        const currentIndicator = indicator.find(i => i.value === contactIndicator);
        if (currentIndicator && cleaned.length > currentIndicator.length) {
            cleaned = cleaned.substring(0, currentIndicator.length);
        }

        setContactNumber(cleaned);
    };

    const isPhoneNumberValid = () => {
        if (!contactNumber || contactNumber.length === 0) return true;
        const currentIndicator = indicator.find(i => i.value === contactIndicator);
        return currentIndicator && contactNumber.length === currentIndicator.length;
    };

    const isParentCategory = () => {
        return !formData.categoryId || formData.categoryId === null;
    };

    return (
        <VStack h="100%" overflow="hidden" p="1.5rem 0" gap="5vh" w="100%">
            {
                state === 1 ?
                    <VStack overflow="hidden" flex={1} gap="5" p="5" w="100%" align="center" justify="flex-start">
                        <VStack flex={1} px={2} w="100%" h="80vh" position="relative" overflow="auto">
                            <LanguageField
                                id="label"
                                errors={formFieldErrors.label}
                                label={t('wrappers.mandatoryLabel', { value: t('fields.label') })}
                                renderElement={(lang) => <Input
                                    value={formData.label[lang]}
                                    onChange={(e) => {
                                        handleInputChange('label', {...formData.label, [lang]: e?.target?.value ?? ''})
                                    }}
                                    placeholder={t('resources.resourceTitle')}
                                />}
                            />
                            <HStack w="100%" gap="2rem">
                                <FileInput
                                    hasPreview
                                    onChange={(file) => handleInputChange('thumbnail', file)}
                                    file={formData.thumbnail}
                                    minH="20vw"
                                    maxH="20vw"
                                    minW="20vw"
                                    maxW="20vw"
                                />
                                <VStack>
                                    <Field
                                        label={t('wrappers.nonMandatoryLabel', { value: t('objects.category_one') })}
                                        errors={formFieldErrors.categoryId}
                                        display="flex"
                                        alignItems="center"
                                        mb="2vh"
                                    >
                                        <NestedSelect
                                            defaultValue={formData?.categoryId ? [formData?.categoryId] : []}
                                            plainTree={categoriesTree}
                                            placeholder={t('misc.selectACategory')}
                                            onValueChange={(e) => handleInputChange('categoryId', e?.value?.[0] ?? null)}
                                        />
                                    </Field>
                                    <Field
                                        label={t('wrappers.nonMandatoryLabel', { value: t('fields.order') })}
                                        errors={formFieldErrors.sortOrder}
                                        display="flex"
                                        alignItems="center"
                                        mb="2vh"
                                    >
                                        <NumberInput.Root
                                            value={formData.sortOrder}
                                            precision={0}
                                            step={1}
                                            onValueChange={(e) => handleInputChange('sortOrder', e.valueAsNumber)}
                                        >
                                            <NumberInput.Input/>
                                            <NumberInput.Control>
                                                <NumberInput.IncrementTrigger/>
                                                <NumberInput.DecrementTrigger/>
                                            </NumberInput.Control>
                                        </NumberInput.Root>
                                    </Field>
                                </VStack>
                                <VStack>
                                    <Field
                                        label={t('wrappers.nonMandatoryLabel', { value: t('fields.contact_name') })}
                                    >
                                        <Input
                                            value={formData.contact?.name || ''}
                                            onChange={(e) => handleInputChange('contact', {
                                                ...(formData.contact || {}),
                                                name: e?.target?.value ?? ''
                                            })}
                                            disabled={!isParentCategory()}
                                            placeholder={!isParentCategory() ? t('misc.contactOnlyForParentCategories') : ''}
                                        />
                                    </Field>
                                    <HStack w="100%" gap="1rem" alignItems="flex-start">
                                        <Field
                                            label={t('wrappers.nonMandatoryLabel', { value: t('fields.contact_indicator') })}
                                            flex={1}
                                        >
                                            <NestedSelect
                                                defaultValue={contactIndicator ? [contactIndicator] : []}
                                                plainTree={indicator}
                                                onValueChange={(e) => {
                                                    const value = e?.value?.[0] ?? '33';
                                                    setContactIndicator(value);
                                                }}
                                                disabled={!isParentCategory()}
                                            />
                                        </Field>
                                        <Field
                                            label={t('wrappers.nonMandatoryLabel', { value: t('fields.contact_number') })}
                                            flex={1}
                                        >
                                            <Input
                                                value={contactNumber}
                                                onChange={(e) => handlePhoneNumberChange(e?.target?.value ?? '')}
                                                disabled={!isParentCategory()}
                                                placeholder={!isParentCategory() ? t('misc.contactOnlyForParentCategories') : '752975839'}
                                            />
                                        </Field>
                                    </HStack>
                                </VStack>
                            </HStack>
                        </VStack>
                    </VStack>
                    :
                    <VStack flex={1} w="100%" align="center" justify="flex-start">
                        <Spinner size="lg"/>
                    </VStack>
            }
            <Button
                variant="primary"
                mt="auto"
                height="auto"
                borderRadius=".5rem"
                style={{padding: '1rem 1.5rem'}}
                onClick={() => handleFormSubmit(async (data) => {
                    if (resourceCategory) {
                        return api.ApiService.update('category', resourceCategory.id, data);
                    }
                    return api.ApiService.create('category', data);
                }, props?.successCallback)
                }
            >
                {t('actions.validate')}
            </Button>
        </VStack>
    );
};

export default CreateOrEditResourceCategory;
